import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import DocumentChatShell from '../shells/DocumentChatShell';
import AssistantBotRow from '../shells/AssistantBotRow';
import { useDocumentChatNavigation } from '../shells/useDocumentChatNavigation';
import ChatSaveErrorBanner from '../components/ChatSaveErrorBanner';
import PdfPreviewActionButton from '../components/PdfPreviewActionButton';
import DocumentChatLayout from '../layout/DocumentChatLayout';
import { interpolateI18n } from '../utils/interpolateI18n';
import { useChatThreadScroll } from '../hooks/useChatThreadScroll';
import { useStaggeredPreviewCta } from '../hooks/useStaggeredPreviewCta';
import EmploymentContractChatComposer, {
  EmploymentContractDeclinedComposer,
} from './EmploymentContractChatComposer';
import EmploymentContractChatThread from './EmploymentContractChatThread';
import { useEmploymentContractFlow } from './useEmploymentContractFlow';

const FIRST_ASSISTANT_DELAY_MS = 1000;
const FOLLOW_ASSISTANT_GAP_MS = 650;
const DEFAULT_MESSAGES_PREFIX = 'submitDocuments.employmentContract';

export interface EmploymentContractChatFlowProps {
  documentTitle: string;
  formCode?: string;
  /** i18n key prefix under submitDocuments.*, e.g. employmentContract or pledge */
  messagesPrefix?: string;
  currentEmail?: string | null;
  invitationId?: string | null;
  onBack: () => void;
  onOpenUnsignedPreview?: () => void;
  onOpenSignedPreview?: () => void;
  onDeclinedContinue?: () => void | Promise<void>;
}

const EmploymentContractChatFlow: React.FC<EmploymentContractChatFlowProps> = ({
  documentTitle,
  formCode,
  messagesPrefix = DEFAULT_MESSAGES_PREFIX,
  currentEmail,
  invitationId,
  onBack,
  onOpenUnsignedPreview,
  onOpenSignedPreview,
  onDeclinedContinue,
}) => {
  const { t } = useLanguage();
  const { exitToChecklist } = useDocumentChatNavigation(onBack);
  const flow = useEmploymentContractFlow(currentEmail, invitationId, t, formCode, messagesPrefix);
  const { msg } = flow;

  const [revealPhase, setRevealPhase] = useState(0);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const previewIntro = useMemo(
    () => interpolateI18n(msg('previewIntro'), { documentTitle }),
    [msg, documentTitle]
  );
  const signedPreviewIntro = useMemo(
    () => interpolateI18n(msg('signedPreviewIntro'), { documentTitle }),
    [msg, documentTitle]
  );

  const showUnsignedCta = flow.awaitingUnsignedPreview && !flow.isLoading;
  const showSignedCta =
    flow.values.agree === 'true' && !flow.materialsDeclined && !flow.isLoading;
  const { showIntro: showUnsignedIntro, showCta: showUnsignedButton } =
    useStaggeredPreviewCta(showUnsignedCta);
  const { showIntro: showSignedIntro, showCta: showSignedButton } =
    useStaggeredPreviewCta(showSignedCta && flow.values.documentRead === 'yes');

  useEffect(() => {
    if (flow.isLoading) return;
    setRevealPhase(0);
    const t1 = window.setTimeout(() => setRevealPhase(1), FIRST_ASSISTANT_DELAY_MS);
    const t2 = window.setTimeout(
      () => setRevealPhase(2),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS
    );
    const t3 = window.setTimeout(
      () => setRevealPhase(3),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS * 2
    );
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [documentTitle, flow.isLoading]);

  useChatThreadScroll(threadEndRef, [
    revealPhase,
    flow.committed.length,
    flow.awaitingAgree,
    flow.materialsDeclined,
    showUnsignedIntro,
    showSignedIntro,
  ]);

  const canShowThread = revealPhase >= 3 && !flow.isLoading;

  return (
    <DocumentChatShell onBack={exitToChecklist} cancelLabel={t('submitDocuments.chat.cancelProcedure')}>
      <DocumentChatLayout
        thread={
          <>
            {revealPhase >= 1 && <AssistantBotRow animateIn>{msg('intro1')}</AssistantBotRow>}
            {revealPhase >= 2 && <AssistantBotRow animateIn>{msg('intro2')}</AssistantBotRow>}
            {flow.isLoading ? (
              revealPhase >= 2 && <AssistantBotRow animateIn>{msg('loading')}</AssistantBotRow>
            ) : canShowThread ? (
              <EmploymentContractChatThread flow={flow} />
            ) : null}
            {showUnsignedIntro && canShowThread && (
              <AssistantBotRow animateIn>{previewIntro}</AssistantBotRow>
            )}
            {flow.values.agree === 'true' && showSignedIntro && canShowThread && (
              <AssistantBotRow animateIn>{msg('completeThankYou')}</AssistantBotRow>
            )}
            {flow.values.agree === 'true' && showSignedIntro && canShowThread && (
              <AssistantBotRow animateIn>{signedPreviewIntro}</AssistantBotRow>
            )}
            <div ref={threadEndRef} aria-hidden />
            <div ref={flow.threadEndRef} aria-hidden />
          </>
        }
        composer={
          <>
            <ChatSaveErrorBanner message={flow.saveError} />
            {flow.materialsDeclined && onDeclinedContinue && canShowThread && (
              <EmploymentContractDeclinedComposer onContinue={() => void onDeclinedContinue()} />
            )}
            {showUnsignedButton && onOpenUnsignedPreview && canShowThread && (
              <PdfPreviewActionButton
                label={msg('previewPdf')}
                onClick={() => {
                  void (async () => {
                    const ok = await flow.markDocumentRead();
                    if (ok) onOpenUnsignedPreview();
                  })();
                }}
              />
            )}
            {showSignedButton && onOpenSignedPreview && canShowThread && (
              <PdfPreviewActionButton
                label={msg('previewSignedPdf')}
                onClick={() => {
                  void (async () => {
                    const ok = await flow.handleFinalComplete();
                    if (ok) onOpenSignedPreview();
                  })();
                }}
              />
            )}
            {!flow.materialsDeclined && !flow.isLoading && canShowThread ? (
              <EmploymentContractChatComposer flow={flow} />
            ) : null}
          </>
        }
      />
    </DocumentChatShell>
  );
};

export default EmploymentContractChatFlow;
